export {};

/**
 * Variant data sharing — PDP page-weight optimization.
 *
 * Background:
 *   The variant pickers (variant-selects, variant-radios, variant-handler,
 *   variant-selects-case-type-filter) historically read their variants by
 *   parsing a child <script type="application/json">{{ product.variants | json }}</script>
 *   embedded inside each <variant-*> element. snippets/product-info.liquid
 *   was emitting that blob up to ~10× per PDP (once per picker-type branch
 *   and once per nested picker), bloating the HTML by several hundred KB.
 *
 * What this file does:
 *   Intercepts customElements.define for the four variant component names and
 *   patches their getVariantData() to resolve data in this order:
 *     1. A locally-scoped <script type="application/json"> child (backwards
 *        compatibility for sections that still embed the blob inline —
 *        featured-product, custom-product-header, etc.).
 *     2. The section-level shared element with id="VariantData-{section.id}"
 *        emitted once per section by product-info.liquid.
 *     3. An empty array (graceful degradation).
 *
 * Load order requirement:
 *   This file MUST execute BEFORE product-variant.js so the prototype is
 *   patched before any custom-element constructor fires. Both scripts are
 *   loaded with `defer`; defer scripts run in document order, so as long as
 *   <script src="variant-data-shared.js" defer> appears before
 *   <script src="product-variant.js" defer> in the markup, ordering holds.
 *   This is critical for VariantSelectsCaseTypeFilter, which calls
 *   getVariantData() synchronously in its constructor during element upgrade.
 */

declare global {
  interface Window {
    __kmVariantSharedInstalled?: boolean;
  }
}

interface PatchableElement extends HTMLElement {
  variantData?: unknown[];
  __kmShared?: boolean;
  getVariantData?(): unknown[];
  dataset: DOMStringMap;
}

interface PatchableConstructor {
  new (...args: unknown[]): PatchableElement;
  prototype: PatchableElement;
}

(function () {
  if (window.__kmVariantSharedInstalled) return;
  window.__kmVariantSharedInstalled = true;

  const CE = window.customElements;
  if (!CE || typeof CE.define !== 'function') return;

  const TARGETS: Record<string, 1> = {
    'variant-selects': 1,
    'variant-radios': 1,
    'variant-handler': 1,
    'variant-selects-case-type-filter': 1,
  };

  const SHARED_ID_PREFIX = 'VariantData-';

  function resolve(el: PatchableElement): unknown[] {
    if (el.variantData) return el.variantData;

    const local = el.querySelector<HTMLScriptElement>('[type="application/json"]');
    if (local?.textContent) {
      try {
        return (el.variantData = JSON.parse(local.textContent) as unknown[]);
      } catch (err) {
        console.warn('[KM variant-data] local parse failed', err);
      }
    }

    const sectionId = el.dataset.section;
    if (sectionId) {
      const shared = document.getElementById(SHARED_ID_PREFIX + sectionId);
      if (shared?.textContent) {
        try {
          return (el.variantData = JSON.parse(shared.textContent) as unknown[]);
        } catch (err) {
          console.error('[KM variant-data] shared parse failed', err);
        }
      }
    }

    return (el.variantData = []);
  }

  function patch(Cls: PatchableConstructor): void {
    if (!Cls?.prototype) return;
    if (!Object.prototype.hasOwnProperty.call(Cls.prototype, 'getVariantData')) return;
    if (Cls.prototype.__kmShared) return;
    Cls.prototype.__kmShared = true;
    Cls.prototype.getVariantData = function (this: PatchableElement) { return resolve(this); };
  }

  const origDefine = CE.define.bind(CE) as typeof CE.define;
  CE.define = function (
    name: string,
    ctor: CustomElementConstructor,
    opts?: ElementDefinitionOptions
  ): void {
    if (TARGETS[name]) {
      try {
        patch(ctor as unknown as PatchableConstructor);
      } catch (err) {
        console.error('[KM variant-data] patch failed for ' + name, err);
      }
    }
    return origDefine(name, ctor, opts);
  };

  // Defensive: patch any already-registered class (covers hot-reload / edge cases).
  Object.keys(TARGETS).forEach((name) => {
    const existing = CE.get(name);
    if (existing) patch(existing as unknown as PatchableConstructor);
  });
})();
